import { columnIcons } from './column-icons'
import { useLayoutEffect, useState } from 'react'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { MenuItemContent } from '@/components/menu/MenuItemContent'
import { getMenuItemClassName } from '@/components/menu/menu-item-styles'
import { IconButton } from '@/components/icon-button/IconButton'
import { Edit, TrashDelete, OverflowMenu, Hide } from '@/assets/icons'
import Pin from '@/assets/icons/pin.svg?react'
import MoveLeft from '@/assets/icons/move-left.svg?react'
import MoveRight from '@/assets/icons/move-right.svg?react'
import Sort from '@/assets/icons/table-sort.svg?react'
import ChevronRight from '@/assets/icons/chevron-right.svg?react'
import { ColumnKind, type TableQuery } from '../schemas'
import { moveColumn, type TableLayout } from '../layout'
export default function TableColumnHeader({
  id,
  name,
  kind,
  isFile = false,
  sort,
  onSort,
  onEdit,
  onDelete,
  manualEntry = false,
  pendingAnalysis = false,
  analysisRunning = false,
  saving,
  layoutDisabled,
  layout,
  order,
  columnIds,
  onLayoutChange,
  onColumnHidden,
}: {
  id: string
  name: string
  kind: ColumnKind
  isFile?: boolean
  sort?: TableQuery['sorts'][number]
  onSort?: (descending?: boolean) => void
  onEdit?: () => void
  onDelete?: () => void
  manualEntry?: boolean
  pendingAnalysis?: boolean
  analysisRunning?: boolean
  saving: boolean
  layoutDisabled: boolean
  layout: TableLayout
  order: string[]
  columnIds: string[]
  onLayoutChange?: (update: (layout: TableLayout) => TableLayout) => void
  onColumnHidden?: () => void
}) {
  const KindIcon = isFile ? columnIcons.file : columnIcons[kind]
  const HeaderLabel = onSort ? 'button' : 'div'
  const canDrag = Boolean(onLayoutChange) && !layoutDisabled
  const [labelNode, setLabelNode] = useState<HTMLSpanElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const isTouchOnly = useIsTouchOnlyDevice()
  useLayoutEffect(() => {
    if (!labelNode) return
    const measure = () => setIsTruncated(labelNode.scrollHeight > labelNode.clientHeight || labelNode.scrollWidth > labelNode.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(labelNode)
    return () => observer.disconnect()
  }, [labelNode, name, sort?.descending])
  const peers = order.filter((peer) => !layout.hidden.includes(peer) && layout.pinned.includes(peer) === layout.pinned.includes(id))
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 px-4 py-2">
      <div className="flex min-h-7 min-w-0 items-center py-1 [overflow-wrap:anywhere]">
        <HeaderLabel
          draggable={canDrag}
          className={`w-full text-left ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
          {...(onSort ? { type: 'button' as const, 'aria-label': `Sort ${name}`, onClick: () => onSort() } : {})}
        >
          <span className="flex items-center gap-2 font-medium leading-5">
            <span className="relative h-4 w-4 shrink-0 text-brand-neutral-600" role="img" aria-label={`${isFile ? 'file' : kind} column`}>
              <KindIcon className={`h-4 w-4 ${canDrag ? 'group-hover/header:opacity-0' : ''}`} aria-hidden />
              {canDrag && (
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/header:opacity-100" aria-hidden>
                  ⠿
                </span>
              )}
            </span>
            <span className="min-w-0">
              <Tooltip title={isTruncated && !isTouchOnly ? name : ''} hoverDelayMs={500} disableInteractive>
                <span ref={setLabelNode} className="block truncate">
                  {name}
                </span>
              </Tooltip>
            </span>
            {sort && (
              <span className="shrink-0" aria-hidden>
                {sort.descending ? '↓' : '↑'}
              </span>
            )}
          </span>
        </HeaderLabel>
      </div>
      {(onEdit || onSort || onDelete || onLayoutChange) && (
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger asChild>
            <IconButton
              className="shrink-0 cursor-pointer opacity-0 group-hover/header:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
              wrapperClassName="cursor-pointer"
              icon={<OverflowMenu />}
              size="sm"
              variant="plain"
              color="neutral"
              aria-label={`Column actions for ${name}`}
              disabled={saving || layoutDisabled}
            />
          </DropdownMenu.Trigger>
          <MenuContent align="end" className="min-w-[180px]">
            {onEdit && (
              <MenuItemGroup>
                <MenuItem label="Edit column" icon={Edit} variant="neutral" onSelect={onEdit} />
              </MenuItemGroup>
            )}
            {onSort && (
              <>
                {onEdit && <MenuSeparator />}
                <MenuItemGroup>
                  <DropdownMenu.Sub>
                    <DropdownMenu.SubTrigger className={getMenuItemClassName({ variant: 'neutral' })}>
                      <MenuItemContent label="Sort by" icon={Sort} trailingIcon={ChevronRight} variant="neutral" />
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.SubContent
                        sideOffset={2}
                        className="z-[1050] min-w-[150px] rounded-lg border border-brand-neutral-200 bg-white shadow-menu"
                      >
                        <MenuItemGroup>
                          <MenuItem label="Ascending" variant="neutral" selected={sort?.descending === false} onSelect={() => onSort(false)} />
                          <MenuItem label="Descending" variant="neutral" selected={sort?.descending === true} onSelect={() => onSort(true)} />
                        </MenuItemGroup>
                      </DropdownMenu.SubContent>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Sub>
                </MenuItemGroup>
              </>
            )}
            {onLayoutChange && (
              <>
                {(onEdit || onSort) && <MenuSeparator />}
                <MenuItemGroup>
                  <MenuItem
                    label={layout.pinned.includes(id) ? 'Unpin column' : 'Pin to left'}
                    icon={Pin}
                    variant="neutral"
                    onSelect={() =>
                      onLayoutChange((current) => ({
                        ...current,
                        pinned: current.pinned.includes(id) ? current.pinned.filter((columnId) => columnId !== id) : [...current.pinned, id],
                      }))
                    }
                  />
                  <MenuItem
                    label="Hide"
                    icon={Hide}
                    variant="neutral"
                    onSelect={() => {
                      onColumnHidden?.()
                      onLayoutChange((current) => ({ ...current, hidden: [...current.hidden, id] }))
                    }}
                  />
                  <MenuItem
                    label="Move left"
                    icon={MoveLeft}
                    variant="neutral"
                    disabled={peers.indexOf(id) === 0}
                    onSelect={() => onLayoutChange((current) => moveColumn(columnIds, current, id, peers[peers.indexOf(id) - 1]))}
                  />
                  <MenuItem
                    label="Move right"
                    icon={MoveRight}
                    variant="neutral"
                    disabled={peers.indexOf(id) === peers.length - 1}
                    onSelect={() => onLayoutChange((current) => moveColumn(columnIds, current, id, peers[peers.indexOf(id) + 1]))}
                  />
                </MenuItemGroup>
              </>
            )}
            {onDelete && (
              <>
                {(onEdit || onSort || onLayoutChange) && <MenuSeparator />}
                <MenuItemGroup>
                  <MenuItem label="Delete" icon={TrashDelete} variant="destructive" onSelect={onDelete} />
                </MenuItemGroup>
              </>
            )}
          </MenuContent>
        </DropdownMenu.Root>
      )}
      {(manualEntry || pendingAnalysis || analysisRunning) && (
        <div className="col-start-1 min-w-0 text-xs font-normal text-brand-neutral-600">
          {manualEntry && <span className="block">Manual entry</span>}
          {pendingAnalysis && <span className="inline-block rounded border border-brand-neutral-300 bg-brand-neutral-100 px-1">Pending analysis</span>}
          {analysisRunning && <span className="block">Running</span>}
        </div>
      )}
    </div>
  )
}
